import {UserProfile} from "../models/user.ts";
import {createContext, ReactNode, useContext, useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {ProblemDetail} from "../types/problem-details.ts";
import {AUTH_INVALID_EVENT} from "@/lib/auth-events.ts";
import {readUserFromToken, tokenIsStillValid} from "@/lib/auth-token.ts";
import ChangePasswordDialog from "@/components/change-password-dialog.tsx";
import ChangeEmailDialog from "@/components/change-email-dialog.tsx";
import SessionExpiredDialog from "@/components/session-expired-dialog.tsx";

type UserContextType = {
    user: UserProfile | null;
    token: string | null;
    loginUser: (username: string, password: string) => Promise<void | ProblemDetail>;
    logout: () => void;
    isLoggedIn: () => boolean;
    isAdmin: () => boolean;
    hasRole: (role: string) => boolean;

    // Change password dialog - used via dropdown menu and user edit page (admins only)
    showChangePasswordDialog: boolean;
    openChangePasswordDialog: () => void;
    closeChangePasswordDialog: () => void;

    // Change email dialog - used via dropdown menu
    showChangeEmailDialog: boolean;
    openChangeEmailDialog: () => void;
    closeChangeEmailDialog: () => void;
};

type LoginResponseType = {
    accessToken: string,
}

type Props = { children: ReactNode };

const UserContext = createContext<UserContextType>({} as UserContextType);

export const UserProvider = ({children}: Props) => {
    const navigate = useNavigate();
    const [token, setToken] = useState<string | null>(null);
    const [user, setUser] = useState<UserProfile | null>(null);
    const [isReady, setIsReady] = useState(false);
    const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
    const [showChangeEmailDialog, setShowChangeEmailDialog] = useState(false);
    const [showSessionExpiredDialog, setShowSessionExpiredDialog] = useState(false);

    useEffect(() => {
        // Legacy cleanup: earlier versions persisted a separate "user" entry which is
        // no longer trusted. The user profile is derived from JWT claims in the
        // server-issued token; authorization decisions remain server-side.
        localStorage.removeItem("user");

        const storedToken = localStorage.getItem("token");
        if (storedToken && tokenIsStillValid(storedToken)) {
            const profile = readUserFromToken(storedToken);
            if (profile) {
                setUser(profile);
                setToken(storedToken);
            } else {
                localStorage.removeItem("token");
            }
        }
        setIsReady(true);
    }, []);

    async function loginUser(username: string, password: string): Promise<void | ProblemDetail> {
        // Timeout nach 10 Sekunden
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        
        try {
            const response = await fetch(`${import.meta.env.VITE_NBH_API_ROOT}/auth/login`, {
                method: 'POST',
                headers: new Headers({
                    'Content-Type': 'application/json'
                }),
                body: JSON.stringify({'username': username, 'password': password}),
                signal: controller.signal
            })

            clearTimeout(timeoutId);
            
            if (response.ok) {
                const tokenResponse = await response.json() as LoginResponseType
                const actualToken = tokenResponse.accessToken;

                const userObj = readUserFromToken(actualToken);
                if (!userObj) {
                    return Promise.reject({
                        type: "about:blank",
                        title: "Invalid token",
                        status: 500,
                        detail: "Server issued a token without the expected claims.",
                    });
                }

                localStorage.setItem("token", actualToken);

                setToken(actualToken);
                setUser(userObj);

                window.dispatchEvent(new CustomEvent('auth:login:success', {
                    detail: { userName: userObj.userName, role: userObj.role }
                }));

                return Promise.resolve()
            } else if (response.headers.get("Content-Type")?.includes("json")) {
                const error: Promise<ProblemDetail> = await response.json()
                return Promise.reject(error)
            } else {
                return Promise.reject({type: "about:blank", "title": "Unknown error", status: response.status})
            }
        } catch (error: any) {
            clearTimeout(timeoutId);
            if (error.name === 'AbortError') {
                return Promise.reject({
                    type: "about:blank", 
                    title: "Request timeout", 
                    status: 408,
                    detail: "Login request timed out. Please try again."
                })
            }
            return Promise.reject({
                type: "about:blank", 
                title: "Network error", 
                status: 0,
                detail: error.message
            })
        }
    }

    const isLoggedIn = () => {
        return !!user;
    }

    const isAdmin = () => {
        return !!user && user.role === "ADMIN"
    }

    const logout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setUser(null);
        setToken("");
        navigate("/");
    }

    useEffect(() => {
        function handleAuthInvalid() {
            if (localStorage.getItem("token")) {
                setShowSessionExpiredDialog(true);
            }
        }

        window.addEventListener(AUTH_INVALID_EVENT, handleAuthInvalid);
        return () => window.removeEventListener(AUTH_INVALID_EVENT, handleAuthInvalid);
    }, []);

    const handleSessionExpiredConfirm = () => {
        setShowSessionExpiredDialog(false);
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setUser(null);
        setToken("");
        navigate("/login", {replace: true});
    };

    const openChangePasswordDialog = () => {
        setShowChangePasswordDialog(true);
    };

    const closeChangePasswordDialog = () => {
        setShowChangePasswordDialog(false);
    };

    const openChangeEmailDialog = () => {
        setShowChangeEmailDialog(true);
    };

    const closeChangeEmailDialog = () => {
        setShowChangeEmailDialog(false);
    };

    const hasRole = (role: string) => {
        if (!user) return false;
        return user.role === role;
    };

    return (
        <UserContext.Provider value={{
            loginUser,
            user,
            token,
            logout,
            isLoggedIn,
            isAdmin,
            hasRole,
            showChangePasswordDialog,
            openChangePasswordDialog,
            closeChangePasswordDialog,
            showChangeEmailDialog,
            openChangeEmailDialog,
            closeChangeEmailDialog,
        }}>
            {isReady ? (
                <>
                    {children}
                    {showChangePasswordDialog && user && (
                        <ChangePasswordDialog
                            effectedUserId={user.userId}
                            standalone={true}
                            onClose={closeChangePasswordDialog}
                            isInitiallyOpen={true}
                            isOwnPassword={true}
                        />
                    )}
                    {showChangeEmailDialog && user && (
                        <ChangeEmailDialog
                            onClose={closeChangeEmailDialog}
                            isInitiallyOpen={true}
                        />
                    )}
                    <SessionExpiredDialog
                        isOpen={showSessionExpiredDialog}
                        onConfirm={handleSessionExpiredConfirm}
                    />
                </>
            ) : null}
        </UserContext.Provider>
    );
}

export const useAuth = () => useContext(UserContext)
